import { gql, useQuery } from '@apollo/client'

import {
  PendingPurchaseItemDetailFragment,
  PendingPurchaseItemDetailFragmentDoc,
  StoreItemUnsettledDetailQuery,
  StoreItemUnsettledDetailQueryVariables,
  UserAddressFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { addCollectionToCollectionFee } from '@/core/cart'
import { convertUserCartItemsToSku } from '@/core/sku'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ pendingPurchaseId: string }>()
  const { data, loading } = useQuery<
    StoreItemUnsettledDetailQuery,
    StoreItemUnsettledDetailQueryVariables
  >(QUERY, {
    variables: {
      pendingPurchaseId: router.query.pendingPurchaseId,
    },
  })

  return {
    state: {
      loading,
      pendingPurchaseItem:
        data?.pendingPurchaseItem as PendingPurchaseItemDetailFragment,
      orderItems: convertUserCartItemsToSku(
        data?.pendingPurchaseItem.items
      ) as PendingPurchaseItemDetailFragment['items'],
      fullName: data?.user?.fullName ?? '',
      address: data?.user?.address ?? null,
      phoneNumber: data?.user?.phoneNumber || '',
      isPhoneNumberVerified: !!data?.user?.isPhoneNumberVerified,
      artist: data?.pendingPurchaseItem?.artist ?? null,
      systemFee: addCollectionToCollectionFee(
        data?.pendingPurchaseItem.checkoutFees?.collections,
        data?.pendingPurchaseItem?.items?.map(({ item }) => item.collection)
      ),
      shippingFee: data?.pendingPurchaseItem?.checkoutFees?.shipping ?? null,
      konbiniFee: data?.pendingPurchaseItem.checkoutFees?.konbini ?? null,
      formattedPrepaidPointBarance:
        data?.prepaidPointBalance?.formattedPrepaidPointBarance ?? 0,
    },
    actions: {},
  }
})

const QUERY = gql`
  query StoreItemUnsettledDetail($pendingPurchaseId: ID!) {
    pendingPurchaseItem(pendingPurchaseId: $pendingPurchaseId) {
      ...PendingPurchaseItemDetail
    }
    user {
      id
      fullName
      phoneNumber
      formattedPhoneNumber @client
      isPhoneNumberVerified
      ...UserAddress
    }
    prepaidPointBalance {
      lastUpdatedAt
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
  }
  ${PendingPurchaseItemDetailFragmentDoc}
  ${UserAddressFragmentDoc}
`
